import {
  AGENT_QUEUE_STATES,
  type AgentQueueItemWire,
  type AgentQueueState,
  type TaskStatus,
} from '@developerz/domain';
import { A } from '@solidjs/router';
import { createUniqueId, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import { ago, untilLabel } from '../lib/time';
import { AgentQueueStuckRow } from './AgentQueueStuckRow';

// One agent's queue, grouped by where each row sits (#6210). Presenter — the rows
// arrive resolved (`AgentQueuePanel` reads them). Each task row names its repo, links
// its pull request on GitHub and, once a box started it, its run timeline. A held row
// says why in the words the plan view uses for the same hold (`fleet.plans.custom.held.*`,
// whose coverage over every `TaskHoldReason` `i18n.test.ts` asserts). The escalation
// queue's Stuck PRs group (#6338, the wider face of #6236's Upcoming) renders its rows
// through `AgentQueueStuckRow` and says once which model rescue hand-overs run on: the
// account's `escalation` pin, or that none is configured (the Escalation card's
// `escalation_unpinned` read, `pinTriggerValue`).

/** The TASK arm of the wire — a row the agent holds. */
type TaskRow = Extract<AgentQueueItemWire, { id: string }>;

const GROUP_KEY: Readonly<Record<AgentQueueState, string>> = {
  queued: 'agents.queue.group.queued',
  running: 'agents.queue.group.running',
  watching: 'agents.queue.group.watching',
  stuck: 'agents.queue.group.stuck',
};

// Total over `TaskStatus`, so a status added to the machine is a compile error here.
// Only the live seven render; the read never serves the other four.
const STATUS_LABEL: Readonly<Record<TaskStatus, string>> = {
  pending: 'fleet.task_status_pending',
  assigned: 'fleet.task_status_assigned',
  in_progress: 'fleet.task_status_in_progress',
  completed: 'fleet.task_status_completed',
  awaiting_ci: 'fleet.task_status_awaiting_ci',
  awaiting_review: 'fleet.task_status_awaiting_review',
  mergeable: 'fleet.task_status_mergeable',
  merged: 'fleet.task_status_merged',
  failed: 'fleet.task_status_failed',
  cancelled: 'fleet.task_status_cancelled',
  paused: 'fleet.task_status_paused',
};

/** Why a queued task row is held, and when a timed hold lifts. */
function holdLine(row: TaskRow): string | null {
  if (row.hold_reason === null) return null;
  // `upstream_group_unmerged` names its upstream slice on the plan view; this row
  // carries no plan, so it says "an earlier slice" rather than leaving `{upstream}`.
  const reason = t(`fleet.plans.custom.held.${row.hold_reason}`, {
    upstream: t('agents.queue.upstream'),
  });
  if (row.hold_until_iso === null) return reason;
  return `${reason} · ${t('agents.queue.lifts_in', { until: untilLabel(row.hold_until_iso) })}`;
}

/** The line under the Stuck PRs heading: the pinned model rescue hand-overs run on, or that there is none. */
function handOverModelLine(model: string | null): string {
  return model === null
    ? t('agents.queue.stuck.no_model')
    : t('agents.queue.stuck.runs_on', { model });
}

/**
 * The queue's rows grouped by state (queued, running, watching, stuck); a state with
 * no rows is omitted. `handOverModel` is the agent's org pin (`pinTriggerValue`), null
 * for Automatic — read only by the Stuck PRs group.
 */
export function AgentQueueList(props: {
  readonly rows: readonly AgentQueueItemWire[];
  readonly handOverModel: string | null;
}): JSX.Element {
  /** The rows in `state`, in the order the server sent them. */
  const inState = (state: AgentQueueState): readonly AgentQueueItemWire[] =>
    props.rows.filter((r) => r.state === state);
  const idBase = createUniqueId();
  return (
    <div class="agent-queue">
      <For each={AGENT_QUEUE_STATES}>
        {(state) => (
          <Show when={inState(state).length > 0}>
            <section
              class="agent-queue__group"
              data-state={state}
              aria-labelledby={`${idBase}-${state}`}
            >
              <h3 class="agent-queue__group-title" id={`${idBase}-${state}`}>
                {t(GROUP_KEY[state])}
              </h3>
              <Show when={state === 'stuck'}>
                <p class="agent-queue__model">{handOverModelLine(props.handOverModel)}</p>
              </Show>
              <ul class="agent-queue__rows">
                <For each={inState(state)}>
                  {(row) =>
                    row.state === 'stuck' ? (
                      <AgentQueueStuckRow row={row} />
                    ) : (
                      <li class="agent-queue__row" data-task={row.id}>
                        <p class="agent-queue__title">{row.title}</p>
                        <p class="agent-queue__meta">
                          <span>{row.repo_full_name}</span>
                          <Show
                            when={row.pr_number}
                            fallback={<span>{t('agents.queue.no_pr')}</span>}
                          >
                            {(n) => (
                              <a
                                href={`https://github.com/${row.repo_full_name}/pull/${n()}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {t('agents.queue.pr', { number: n() })}
                              </a>
                            )}
                          </Show>
                          <span>{t(STATUS_LABEL[row.status])}</span>
                          <span>{t('agents.queue.updated', { ago: ago(row.updated_at_iso) })}</span>
                        </p>
                        <Show when={holdLine(row)}>
                          {(line) => <p class="agent-queue__hold">{line()}</p>}
                        </Show>
                        <Show when={row.run_id}>
                          {(runId) => (
                            <A
                              class="agent-queue__run"
                              href={orgHref(`/fleet/runs/${encodeURIComponent(runId())}`)}
                            >
                              {t('agents.queue.view_run')}
                            </A>
                          )}
                        </Show>
                      </li>
                    )
                  }
                </For>
              </ul>
            </section>
          </Show>
        )}
      </For>
    </div>
  );
}
